import { motion, useScroll } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Ticket, Menu, X } from "lucide-react";
import { TICKET_2027 } from "@/lib/platform";
import { trackInitiateCheckout, prepareStripeCheckout } from "@/lib/metaPixel";

const LINKS = [
  { id: "home", label: "Начало", href: "/" },
  { id: "events", label: "Събития", href: "/#events" },
  { id: "about", label: "За нас", href: "/#about" },
  { id: "gallery", label: "Галерия", href: "/#gallery" },
  { id: "partners", label: "Партньори", href: "/partners" },
];

export function goToTicketCheckout() {
  const checkoutUrl = prepareStripeCheckout({
    url: TICKET_2027.url,
    ticketType: "early_bird_2027",
    value: TICKET_2027.price,
    currency: "EUR",
    contentName: TICKET_2027.contentName,
  });
  trackInitiateCheckout({
    value: TICKET_2027.price,
    currency: "EUR",
    content_name: TICKET_2027.contentName,
    content_ids: [TICKET_2027.contentId],
  });
  window.open(checkoutUrl, "_blank", "noopener,noreferrer");
}

export default function NationalNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { scrollY } = useScroll();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => scrollY.on("change", (y) => setScrolled(y > 40)), [scrollY]);

  const go = (href) => {
    setMobileOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <>
      <motion.header
        data-testid="national-nav"
        className={`fixed top-0 left-0 right-0 z-[70] transition-all duration-300 ${
          scrolled
            ? "bg-[#0B1F3D]/90 backdrop-blur-md border-b border-[#1E3A63] py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between gap-4">
          <button
            onClick={() => go("/")}
            data-testid="national-nav-logo"
            className="font-display uppercase tracking-tight text-lg sm:text-xl text-[#F5EBD9] leading-none"
          >
            Играта <span className="text-[#F5892E]">на</span> Имотите
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.href)}
                data-testid={`national-nav-${l.id}`}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C8D4E6] hover:text-[#F5892E] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => go("/partners")}
              data-testid="national-nav-partner-cta"
              className="hidden md:inline-flex px-5 py-2.5 rounded-full border border-[#F5EBD9]/30 text-[#F5EBD9] font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#F5EBD9] hover:text-[#0B1F3D] transition-all"
            >
              Стани партньор
            </button>
            <button
              onClick={goToTicketCheckout}
              data-testid="national-nav-ticket-cta"
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-xs sm:text-sm uppercase tracking-wider hover:bg-[#F5EBD9] transition-colors"
            >
              <Ticket className="w-4 h-4" strokeWidth={2.4} />
              <span className="hidden sm:inline">Вземи билет — 34 €</span>
              <span className="sm:hidden">34 €</span>
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="national-nav-burger"
              aria-label="Меню"
              className="lg:hidden w-10 h-10 flex items-center justify-center text-[#F5EBD9]"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="national-nav-mobile"
            className="lg:hidden bg-[#0B1F3D]/98 backdrop-blur-md border-t border-[#1E3A63] mt-3 px-6 py-6 flex flex-col gap-5"
          >
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.href)}
                className="text-left font-display uppercase text-2xl text-[#F5EBD9]"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => { setMobileOpen(false); go("/partners"); }}
              className="mt-2 px-6 py-3.5 rounded-full border border-[#F5EBD9]/30 text-[#F5EBD9] font-mono text-xs uppercase tracking-[0.2em]"
            >
              Стани партньор
            </button>
          </motion.div>
        )}
      </motion.header>

      {/* Mobile sticky bottom ticket bar */}
      <div className="fixed bottom-0 left-0 right-0 z-[60] lg:hidden bg-[#0B1F3D]/95 backdrop-blur-md border-t border-[#1E3A63] px-4 py-3">
        <button
          onClick={goToTicketCheckout}
          data-testid="mobile-sticky-ticket"
          className="w-full py-3.5 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-sm uppercase tracking-widest"
        >
          Вземи билет — 34 €
        </button>
      </div>
    </>
  );
}
